import type { FormApi } from 'final-form';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import type { AnyObject, FormSpyRenderProps } from 'react-final-form';
import { FormSpy } from 'react-final-form';
import api from '../../../../api';
import { debounceWithOptions } from '../../../services/lodash++';
import type { Backup } from './types';

const DEBOUNCE_MAX_WAIT = 30000;

type toBackUpFn<T> = (form: FormApi<T>, values: T) => Backup;

type AutoSaverProps<T> = {
    toBackup: toBackUpFn<T>;
} & FormSpyRenderProps<T>;

class AutoSaver<T> extends Component<AutoSaverProps<T>> {
    promise?: Promise<Backup>;

    componentDidMount() {
        this.promise = Promise.resolve(this.createBackup());
    }

    componentDidUpdate() {
        this.save();
    }

    componentWillUnmount() {
        this.promise = undefined;
    }

    createBackup = () => this.props.toBackup(this.props.form, this.props.values);

    save = debounceWithOptions(DEBOUNCE_MAX_WAIT)({ maxWait: DEBOUNCE_MAX_WAIT })(async () => {
        if (!this.promise) {
            return;
        }
        const prevBackup = await this.promise;
        const nextBackup = this.createBackup();
        if (prevBackup && isEqual(prevBackup.texts, nextBackup.texts)) {
            return;
        }
        this.promise = api.backups.save(nextBackup).then(
            () => nextBackup,
            () => ({}) as Backup
        );
    });

    render = () => null;
}

type AutoBackupProps<T> = {
    toBackup: toBackUpFn<T>;
};

export default function FormAutoBackup<T extends AnyObject>({ toBackup }: AutoBackupProps<T>) {
    return (
        <FormSpy<T> subscription={{ values: true }}>
            {renderProps => <AutoSaver<T> {...renderProps} toBackup={toBackup} />}
        </FormSpy>
    );
}

FormAutoBackup.propTypes = {
    toBackup: PropTypes.func.isRequired, // (form, values) => Backup
};
